import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  getValidSteps,
  resolveDisplayedStep,
  submitApplication as submitApplicationApi,
  withdrawApplication as withdrawApplicationApi
} from "./portalUtils";
import { applicationApi } from "../../Store/api/application/applicationApi";
import { isEmptyObject } from "../../MPP-Common-SPA/utils/uiUtility";
import { selectIsLoggedIn, selectCurrentUser } from '../../Store/api/auth/authSelectors';
import { InternalApplicationStatuses } from "../../MPP-Common-SPA/common/constants/statuses";
import { STEP_NAMES } from "../../MPP-Common-SPA/common/constants/names";
import { STEP_TYPES } from "../../MPP-Common-SPA/common/constants/types";
import { URL_SEARCH_PARAMS } from "../../MPP-Common-SPA/common/constants/urlParams";
import { specialSteps } from "./portalContextConstants";
import { selectProgram, selectProgramTemplate } from '../../Store/api/program/programSelectors';
import { useUnsavedChanges } from "../../MPP-Common-SPA/common/unsavedChangesContext";
import { USER_APPLICATION_SESSION_KEY, isPortalActive, isEventTypeActive, EventsEnabled, viewOnlyApplication } from "../../MPP-Common-SPA/utils/globalUtility";
import applicationUtils from "../utils/portalUtility";
import { ErrorMessages } from "../../MPP-Common-SPA/common/constants/validations";
import { useGetProgramFeatureMappingByNameQuery, useGetProgramFeaturesQuery, useGetDynamicFormListQuery } from "../../Store/api/apiSlice";
import { useGetApplicationListQuery, useSaveDraftApplicationMutation, useSubmitApplicationMutation, useApplyValueMappingsMutation } from "../../Store/api/application/applicationApi";
import { EventStatusEnum } from "../../MPP-Common-SPA/programLocations/enums";
import { FeatureNames } from "../../MPP-Common-SPA/featureFlags/features";
import { getAvailableProgramEvents } from "../../MPP-Common-SPA/utils/getAvailableProgramEvents";
import { trackEvent } from "../../MPP-Common-SPA/utils/analytics";
import { NEXT_BUTTON_EVENT, PREVIOUS_BUTTON_EVENT } from "../../MPP-Common-SPA/utils/analytics-constants";
import { useProgramSettingsInfo } from "./useProgramSettingsInfo";
import { SettingNames } from "../../MPP-Common-SPA/programSettings/enums";
import { useAdditionalDetails } from "../../hooks/useAdditionalInformation";


export const usePortalContext = ({userApplication = {}, refetchApplication = undefined, isUninitialized = true, programEvents=[]}) => {
  const dispatch = useDispatch();
  const [saveDraftApplication] = useSaveDraftApplicationMutation();
  const [submitApplicationMutation] = useSubmitApplicationMutation();
  const [applyValueMappingsMutation] = useApplyValueMappingsMutation();

  const isLoggedIn = useSelector(selectIsLoggedIn);
  let user = useSelector(selectCurrentUser);
  const portal = useSelector(selectProgram);
  const template = useSelector(selectProgramTemplate);
  const { data: programEventMapping, isLoading: isProgramEventMappingLoading, isSuccess: isProgramEventMappingLoaded } = useGetProgramFeatureMappingByNameQuery(
    { featureName: FeatureNames.Events, programId: portal.id },
    { skip: !portal.id }
  );

  const { data: programFeatures = {}, isLoading: isProgramFeaturesLoading } = useGetProgramFeaturesQuery(
    portal?.id || '',
    { skip: !portal?.id }
  );

  const { data: applicationList = [], isLoading: isApplicationListLoading } = useGetApplicationListQuery(undefined, {
    skip: !isLoggedIn,
  });

  const { data: dynamicForms = [] } = useGetDynamicFormListQuery();

  const { version, defaults } = template ?? {};
  const [specialStep, setSpecialStep] = useState(null);
  const [isBusy, setIsBusy] = useState(false);
  const [errorModalState, setErrorModalState] = useState('');

  const getStoredUserApplication = () => {
    try {
      return JSON.parse(sessionStorage.getItem(USER_APPLICATION_SESSION_KEY)) ?? {};
    } catch (error) {
      console.error(ErrorMessages.ParseSessionStorage, error);
      return {};
    }
  };

  const replaceUserApplication = (newApplication) => {
    setUserApplicationData(newApplication);
  }

  const [userApplicationData, setUserApplicationData] = useState(() => {
    // Prioritize loading in applications that already exist.
    if(user.isProxyUser){
      // Always clear session for proxy users.
      sessionStorage.removeItem(USER_APPLICATION_SESSION_KEY);
    }

    if (userApplication?.applicationId || userApplication?.uniqueApplicationCode) {
      return userApplication;
    }

    // Use stored application data as a fallback (For returning from login.)
    const storedData = getStoredUserApplication();

    // Validate stored data matches current program/event context
    if (Object.keys(storedData).length > 0) {
      const isValidProgram = portal?.id && storedData?.programId === portal?.id;
      const isValidEvent = !storedData?.eventId || programEvents?.some(event => event.id === storedData?.eventId);
      
      if (isValidProgram && isValidEvent) {
        return storedData;
      } else {
        // Clear invalid session data
        sessionStorage.removeItem(USER_APPLICATION_SESSION_KEY);
      }
    }
    
    return userApplication;
  });

  useEffect(() => {
    sessionStorage.setItem(USER_APPLICATION_SESSION_KEY, JSON.stringify(userApplicationData));
  }, [userApplicationData]);

  useEffect(() => {
    if (userApplication?.applicationData) {
      if (JSON.stringify(userApplication) !== JSON.stringify(userApplicationData)) {
        setUserApplicationData(userApplication);
        // Update the snapshot when backend data is loaded/refreshed
        updateSavedSnapshot(userApplication?.applicationData);
      }
    } else if (userApplication.applicationType) {
      // New application with hint URL param — merge only the applicationType fields so
      // that any pre-existing session progress (other steps) is preserved.
      setUserApplicationData((prev) => {
        if (
          prev?.applicationType === userApplication.applicationType &&
          prev?.applicationData?.stepData?.[STEP_NAMES.Overview]?.applicationType === userApplication.applicationType
        ) {
          return prev;
        }
        return {
          ...prev,
          applicationType: userApplication.applicationType,
          applicationTypeId: userApplication.applicationTypeId,
          applicationData: {
            ...(prev?.applicationData ?? {}),
            stepData: {
              ...(prev?.applicationData?.stepData ?? {}),
              [STEP_NAMES.Overview]: {
                ...(prev?.applicationData?.stepData?.[STEP_NAMES.Overview] ?? {}),
                applicationType: userApplication.applicationType,
              },
            },
          },
        };
      });
    }
  }, [userApplication]);

  const { applicationType, internalStatus } = userApplicationData;

  const activeEvents = !isProgramEventMappingLoading 
    ? (programEvents?.filter(evt => evt.isActive === true && evt.eventStatus === EventStatusEnum.Active) ?? [])
    : [];
  
  const isActivePortal = isPortalActive(portal, user);
  const isEventsEnabled = !isProgramEventMappingLoading && programEventMapping 
    ? EventsEnabled(programEventMapping) 
    : undefined;
  const isActiveEvent = !isProgramEventMappingLoading && isEventsEnabled 
    ? activeEvents.length > 0 
    : undefined;
  
  const availableEvents = useMemo(
    () => !isProgramEventMappingLoading && !isApplicationListLoading && isEventsEnabled && programEvents && applicationList
      ? getAvailableProgramEvents(programEvents, applicationList)
      : [],
    [isProgramEventMappingLoading, isApplicationListLoading, isEventsEnabled, programEvents, applicationList],
  );

  const [selectedEvent, setSelectedEvent] = useState(() => {
    return !isProgramEventMappingLoading 
      ? programEvents?.find(evt => evt.code === applicationType)
      : undefined;
  });

  // Update selectedEvent when applicationType changes
  useEffect(() => {
    if (!isProgramEventMappingLoading && programEvents && applicationType) {
      const event = programEvents.find(evt => evt.code === applicationType);
      setSelectedEvent(event);
    }
  }, [applicationType, programEvents, isProgramEventMappingLoading]);
  
  const { addDirty, removeDirty, clearDirty } = useUnsavedChanges();

  // Snapshot of stepData as last saved to the backend (or loaded from it).
  // Used to detect unsaved changes across all steps, not just the current one.
  const savedStepDataSnapshot = useRef(
    JSON.stringify(userApplicationData?.applicationData?.stepData ?? {})
  );

  // Helper to update the saved snapshot after successful backend operations.
  const updateSavedSnapshot = (applicationData) => {
    savedStepDataSnapshot.current = JSON.stringify(applicationData?.stepData ?? {});
  };

  const getStepData = useCallback(
    (stepName) => {
      const { applicationData } = userApplicationData ?? {};
      const { stepData = {} } = applicationData ?? {};

      return stepData[stepName] ?? {};
    },
    [userApplicationData]
  );

  const { uniqueApplicationCode, applicationId } = userApplicationData;

  const { voucher, benefits } = useAdditionalDetails(internalStatus, applicationId);

  const {
    error = false,
    applicationStatus,
    canEdit = false,
    currentStep = null,
    stepData = {},
    validSteps = [],
    numberOfSteps = 0,
    inReviewMode = false,
    firstStep = null,
    applicationStepNames = [],
    defaultValues = {},
  } = useMemo(() => {
    if (
      isEmptyObject(portal) ||
      isEmptyObject(template) ||
      !userApplicationData
    ) {
      return { error: true };
    }

    const { lastStep, inReviewMode } =
      userApplicationData?.applicationData ?? {};

    const { numberOfSteps, validSteps, firstStep, applicationStepNames, defaultValues } = getValidSteps({
      template,
      dynamicForms,
      user
    });

    const { steps } = template;

    //FATAL: The selected application does not contain the right steps
    if (!steps || steps?.length === 0) {
      return { error: true, numberOfSteps, validSteps, applicationStepNames };
    }

    const {
      internalStatus,
      applicationData,
      canEdit = true,
    } = userApplicationData;

    // For each step that is not shown, apply its defaultValuesWhenHidden.
    // Existing step data takes precedence over the defaults.
    const existingStepData = applicationData?.stepData ?? {};
    const effectiveStepData = { ...existingStepData };
    Object.entries(defaultValues).forEach(([stepName, defaultValuesWhenHidden]) => {
      effectiveStepData[stepName] = {
        ...defaultValuesWhenHidden,
        ...(existingStepData[stepName] ?? {}),
      };
    });

    let displayedStep = lastStep;
    if (!displayedStep) {
      displayedStep = firstStep.name;
    }
    if (internalStatus === InternalApplicationStatuses.Withdrawn) {
      displayedStep = STEP_NAMES.Review;
    }
    // Redirect non-proxy citizens to the Review step for view-only statuses (e.g. PaymentComplete).
    // Proxy users arrive via ?step=<section> from the Internal Portal and must not be redirected.
    if (!user?.isProxyUser && viewOnlyApplication(internalStatus, user)) {
      displayedStep = STEP_NAMES.Review;
    }
    if (EventsEnabled(programEventMapping)) {
      const selectedEvent = programEvents.find(evt => evt.code === applicationType);
      if (selectedEvent && (!isEventTypeActive(programEventMapping, selectedEvent, user) || !isPortalActive(portal, user))) {
        displayedStep = STEP_NAMES.Review;
      }
    }

    // The persisted step may no longer be visible (e.g. Verification hidden after account
    // verification); resolve it to the next visible step before looking it up.
    displayedStep = resolveDisplayedStep(displayedStep, { template, validSteps, firstStep });

    let nextStep = validSteps[displayedStep];

    //FATAL:: probably reached the last step but the application status should have been submitted
    if (!nextStep && (internalStatus !== InternalApplicationStatuses.Submitted || internalStatus !== InternalApplicationStatuses.FollowUp)) {
      return { error: true, numberOfSteps, validSteps };
    }

    //FATAL: no next step and the application is not marked as submittedapplication seem to be finished now
    if (!nextStep) {
      return {
        internalStatus,
        canEdit,
        error: true,
        numberOfSteps,
        validSteps,
        applicationStepNames,
      };
    }

    const { renderer: { props: { type } = {} } = {} } = nextStep;

    //get step data for the particular step
    const stepData = effectiveStepData[`${nextStep.name}`] ?? {};

    return {
      internalStatus,
      canEdit,
      error: false,
      stepData : stepData?.isInternal ? {} : stepData,
      currentStep: nextStep,
      numberOfSteps,
      validSteps,
      firstStep,
      inReviewMode,
      applicationStepNames,
      defaultValues,
    };
  }, [portal, template, userApplicationData, applicationType, programEventMapping, dynamicForms]);

  // Persist defaultValuesWhenHidden into userApplicationData so they are included on save/submit.
  // Existing step data always takes precedence over the defaults.
  useEffect(() => {
    if (isEmptyObject(defaultValues ?? {})) return;
    setUserApplicationData((currentApplicationData) => {
      const existingStepData = currentApplicationData?.applicationData?.stepData ?? {};
      const updatedStepData = Object.entries(defaultValues).reduce(
        (accumulator, [stepName, defaultValuesWhenHidden]) => ({
          ...accumulator,
          [stepName]: { ...defaultValuesWhenHidden, ...(accumulator[stepName] ?? {}) },
        }),
        { ...existingStepData },
      );

      if (JSON.stringify(updatedStepData) === JSON.stringify(existingStepData)) {
        return currentApplicationData;
      }

      return {
        ...currentApplicationData,
        applicationData: {
          ...(currentApplicationData?.applicationData ?? {}),
          stepData: updatedStepData,
        },
      };
    });
  }, [defaultValues]);

  // Compare current stepData against the last-saved snapshot for application steps only.
  // applicationStepNames is computed once by getValidSteps from the program template.
  useEffect(() => {
    if (!isLoggedIn) return;
    const currentStepData = userApplicationData?.applicationData?.stepData ?? {};
    const savedStepData = JSON.parse(savedStepDataSnapshot.current);
    applicationStepNames.concat(STEP_NAMES.Review).forEach((stepName) => {
      const isDirty = JSON.stringify(currentStepData[stepName] ?? {}) !== JSON.stringify(savedStepData[stepName] ?? {});
      if (isDirty) {
        addDirty(stepName);
      } else {
        removeDirty(stepName);
      }
    });
  }, [userApplicationData, isLoggedIn, addDirty, removeDirty, applicationStepNames]);

  const getStepByOrder = useCallback(
    (order) => {
      return Object.values(validSteps).find(step => step.order === order);
    },
    [validSteps]
  );

  const getStepByType = useCallback(
    (type) => {
      for (var id in validSteps) {
        const { renderer: { props: { type: stepType } = {} } = {} } =
          validSteps[id];
        if (stepType === type) {
          return validSteps[id];
        }
      }
    },
    [validSteps]
  );

  const getStepByName = useCallback(
    (stepName) => {
      return (template?.steps ?? []).find(
        (x) => x.name?.toLowerCase() === stepName.toLowerCase()
      );
    },
    [template]
  );

  const moveToStep = useCallback(
    (stepName) => {
      const step = getStepByName(stepName);
      if (step) {
        setUserApplicationData((s) => ({
          ...s,
          applicationData: {
            ...(s?.applicationData ?? {}),
            lastStep: step?.name,
          },
        }));
      }
    },
    [getStepByName]
  );

  const shouldApplyValueMappings = (user, application) => {
    // Value mappings are handled in the api for Proxy user edit flow. Applying them here will cause validation to fail.
    if (!user?.isProxyUser) return true;
    // Value mappings should be applied in new or draft applications for proxy flow.
    const isNewApplication = !application?.internalStatus;
    const isDraft = application?.internalStatus === InternalApplicationStatuses.Draft;
    return isNewApplication || isDraft;
  };

  const updateValueMappings = async (application) => {
    if (!shouldApplyValueMappings(user, application)) return application;
    const result = await applyValueMappingsMutation(JSON.stringify(application));
    const mappedJson = result?.data;
    if (!mappedJson) return application;
    return JSON.parse(mappedJson);
  };

  const moveToReviewStep = useCallback(() => {
      const step = getStepByType(STEP_TYPES.Review);
      setUserApplicationData((s) => ({
        ...s,
        applicationData: {
          ...(s?.applicationData ?? {}),
          lastStep: step?.name,
        },
      }));
  }, [getStepByType]);

  useEffect(() => {
    if (currentStep?.renderer?.props?.type !== STEP_TYPES.Review) return;
    updateValueMappings(userApplicationData).then((mappedApplication) => {
      if (JSON.stringify(mappedApplication) !== JSON.stringify(userApplicationData)) {
        setUserApplicationData(mappedApplication);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep]);

  // Auto-select when there is exactly one event available to this user.
  // Uses availableEvents (not activeEvents) so the check matches what the radio group renders:
  // events that are active AND the user has not already submitted a non-withdrawn/rejected application for.
  useEffect(() => {
    if (
      internalStatus ||
      applicationType ||
      !isEventsEnabled ||
      isProgramEventMappingLoading ||
      availableEvents?.length !== 1
    ) {
      return;
    }

    const event = availableEvents[0];
    setSelectedEvent(event);
    setUserApplicationData((prev) => ({
      ...prev,
      applicationType: event.code,
      applicationTypeId: event.id,
      applicationData: {
        ...(prev?.applicationData ?? {}),
        stepData: {
          ...(prev?.applicationData?.stepData ?? {}),
          [STEP_NAMES.Overview]: {
            ...(prev?.applicationData?.stepData?.[STEP_NAMES.Overview] ?? {}),
            applicationType: event.code,
          },
        },
      },
    }));
  }, [
    isEventsEnabled,
    isProgramEventMappingLoading,
    availableEvents,
    applicationType,
    internalStatus,
  ]);

  const onNext = useCallback(
    async (data, fields, context, getRefData) => {
      let lastStepName = currentStep.name;
      let isReviewStep = false;
      let applicationTypeData = {};

      if(data?.applicationType){
        const eventForApplicationType = programEvents?.find(evt => evt.code === data.applicationType);
        if (eventForApplicationType) {
          applicationTypeData = {
            applicationType: eventForApplicationType.code,
            applicationTypeId: eventForApplicationType.id,
          };
          setSelectedEvent(eventForApplicationType);
        }
      };

      data = {
        ...data,
        ...applicationTypeData
      };    

      const nextStep = getStepByOrder(currentStep.order + 1);
      lastStepName = nextStep?.name;
      isReviewStep = nextStep?.renderer?.props?.type === STEP_TYPES.Review;

      const filteredData = data?.isInternal !== true ? applicationUtils.filterDataByVisibleElements(data, fields, context, getRefData) : data;

      const applicationDataUpdate = {
        ...(userApplicationData?.applicationData ?? {}),
        inReviewMode: isReviewStep,
        lastStep: lastStepName,
        generation: userApplicationData?.applicationData?.generation ?? 0,
        stepData: {
          ...(userApplicationData?.applicationData?.stepData ?? {}),
          [`${currentStep.name}`]: {
            ...filteredData,
          },
        },
      };
      
      const newApplication = {
        ...userApplicationData,
        programId: portal?.id,
        ...applicationTypeData,
        applicationData: applicationDataUpdate,
      };

      setUserApplicationData(newApplication);

      // Send GA event when the next button is clicked. The step is the current step when the button is clicked.
      trackEvent(NEXT_BUTTON_EVENT, {
        "next_button_step": currentStep.name
      });
    }, [isLoggedIn, userApplicationData, currentStep, applicationType, portal?.id]
  );

  const onPrevious = useCallback(
    async (data, fields, context, getRefData) => {
      // Check if we can go back
      if (!currentStep || currentStep === firstStep) {
        // Already at first step, can't go back further
        return;
      }
      // Hidden steps are already excluded from validSteps, so the previous step
      // is always the immediately preceding visible step.
      const lastStep = getStepByOrder(currentStep.order - 1);

      // If no previous step found, don't proceed
      if (!lastStep) {
        console.error('No previous step found');
        return;
      }

      const filteredData = data?.isInternal !== true 
        ? applicationUtils.filterDataByVisibleElements(data, fields, context, getRefData) 
        : data;

      const newApplication = {
        ...userApplicationData,
        applicationData: {
          ...(userApplicationData?.applicationData ?? {}),
          lastStep: lastStep?.name,
          stepData: {
            ...(userApplicationData?.applicationData?.stepData ?? {}),
            [`${currentStep.name}`]: {
              ...filteredData,
            },
          },
        },
      };

      setUserApplicationData(newApplication);

      // Send GA event when the previous button is clicked
      trackEvent(PREVIOUS_BUTTON_EVENT, {
        "previous_button_step": currentStep.name
      });
    },
    [userApplicationData, currentStep, getStepByOrder]
  );

  const onSave = useCallback(
    async (data, stepName) => {
      const newApplication = {
        ...userApplicationData,
        applicationData: {
          ...(userApplicationData?.applicationData ?? {}),
          [`${stepName}`]: {
            ...data,
          },
        },
      };

      setUserApplicationData(newApplication);
    },
    [userApplicationData]
  );

  const addStepData = ({ data, userApplicationData, currentStep }) => {
    let newApplicationData = { ...userApplicationData };
    let applicationTypeData = {};

    // Sets root applicationType variables of applicationType if included in the call.
    if(data?.applicationType){
      const eventForApplicationType = programEvents?.find(evt => evt.code === data.applicationType);
      if (eventForApplicationType) {
        applicationTypeData = {
          applicationType: eventForApplicationType.code,
          applicationTypeId: eventForApplicationType.id,
        };
        setSelectedEvent(eventForApplicationType);
      }
    };

    const applicationDataUpdate = {
      ...(newApplicationData?.applicationData ?? {}),
      generation: newApplicationData?.applicationData?.generation ?? 0,
      stepData: {
        ...(newApplicationData?.applicationData?.stepData ?? {}),
        [`${currentStep.name}`]: {
          ...data,
        },
      },
    };
    
    // Add generation

    newApplicationData = {
      ...newApplicationData,
      ...applicationTypeData,
      applicationData: applicationDataUpdate,
    };

    return newApplicationData;
  };

  const saveStepData = useCallback(
    async (data, fields, context, getRefData) => {

      const filteredData = data?.isInternal !== true ? applicationUtils.filterDataByVisibleElements(data, fields, context, getRefData) : data;

      const newApplicationData = addStepData({
        data: filteredData,
        userApplicationData,
        currentStep,
      });

      const url = new URL(window.location.href);
      const code = url.searchParams.get(URL_SEARCH_PARAMS.Code);
      const stepNameFromUrl = url.searchParams.get(URL_SEARCH_PARAMS.Step);

      // redirect back to the internal portal for proxy user
      if (code && stepNameFromUrl) {
        const success = await saveApplication(newApplicationData);
        updateSavedSnapshot(newApplicationData?.applicationData);
        return success;
      }

      const isUpdated = JSON.stringify(newApplicationData) !== JSON.stringify(userApplicationData);
      setUserApplicationData(newApplicationData);
      return isUpdated;
    },
    [userApplicationData, currentStep]
  );

  const moveToSpecialStep = useCallback((stepName) => {
    setIsBusy(false);
    setErrorModalState('');
    setSpecialStep(stepName);
  }, []);

  const moveBackToApp = useCallback(() => {
    setSpecialStep("");
    
    // Update Redux state
    if (!isUninitialized && refetchApplication && !!uniqueApplicationCode) {
      refetchApplication();
    }
  }, [refetchApplication, isUninitialized, uniqueApplicationCode]);


  const moveToSubmissionRequirement = useCallback(() => {
    setIsBusy(false);
    moveToSpecialStep(specialSteps.SubmissionRequirement);
  }, [moveToSpecialStep]);

  const finishLater = useCallback(
    async (stepData) => {
      setIsBusy(true);
  
      let newApplicationData = { ...userApplicationData };
  
      if (Object.keys(stepData || {}).length !== 0) {
        newApplicationData = addStepData({
          data: stepData,
          userApplicationData,
          currentStep,
        });
      }
  
      let result;
  
      try {
        result = await saveDraftApplication({
          application: JSON.stringify(newApplicationData ?? {}),
          applicationId:
            newApplicationData?.applicationId ?? newApplicationData?.id,
          applicationTypeId: newApplicationData?.applicationTypeId,
        });
      } catch (error) {
        console.error("Network error during finishLater", error);
  
        setIsBusy(false);
        setErrorModalState({ message: ErrorMessages.SaveAndFinishLater });
  
        return false;
      }
  
      if (result?.error) {
        const { data } = result.error;

        setIsBusy(false);
        setErrorModalState({
          errorCode: data?.errorCode,
          status: result.error?.status,
          message: data?.message || ErrorMessages.SaveAndFinishLater,
        });

        return false;
      }
  
      const data = result.data;
  
      const savedAppData = data.applicationDataJson
        ? JSON.parse(data.applicationDataJson).applicationData
        : (data.applicationData ?? {});
      updateSavedSnapshot(savedAppData);
  
      setUserApplicationData(() => ({
        programId: data.programId,
        applicationType: data.applicationType,
        applicationTypeId: data.applicationTypeId,
        applicationId: data.applicationId ?? data.id,
        internalStatus: data.internalStatus,
        uniqueApplicationCode: data.uniqueApplicationCode,
        applicationData: savedAppData,
      }));
  
      moveToSpecialStep(specialSteps.FinishLater);
  
      return true;
    },
    [userApplicationData, currentStep, saveDraftApplication]
  );

  const copyApplicationData = useCallback(
    ({applicationToCopy}) => {
      var newApplicationData = {};

      var { id, applicationData: { applicationData: { stepData } = {} } = {} } = applicationToCopy;

      Object.keys(validSteps).forEach((key) => {
        if (validSteps[key].allowCopyFromPreviousApplication) {
          newApplicationData = {
            ...newApplicationData,
            [key]: { ...(stepData[key] ?? {}) },
          };
        }
      });

      setUserApplicationData((s) => ({
        ...s,
        copyApplied: true,
        applicationData: {
          ...(s?.applicationData ?? {}),
          copiedFrom: id,
          stepData: {
            ...(s?.applicationData?.stepData ?? {}),
            ...newApplicationData,
          },
        },
      }));
    },
    [validSteps, setUserApplicationData]
  );

  const refuseCopyApplicationData = useCallback(() => {
    setUserApplicationData((s) => ({
      ...s,
      copyApplied: true,
    }));
  }, [setUserApplicationData]);

  const submitApplication = useCallback(
    async (
      applicationOverride = null,
      stepData = null,
      fields = null,
      context = null,
      getRefData = null
    ) => {
      setIsBusy(true);
  
      const filteredStepData =
        stepData?.isInternal !== true
          ? applicationUtils.filterDataByVisibleElements(
              stepData,
              fields,
              context,
              getRefData
            )
          : stepData;
  
      const appData =
        Object.keys(filteredStepData || {}).length !== 0
          ? addStepData({
              data: filteredStepData,
              userApplicationData: applicationOverride ?? userApplicationData,
              currentStep,
            })
          : applicationOverride ?? userApplicationData;
  
      const prevAppInternalStatus = appData.internalStatus;
  
      let result;
      try {
        result = await submitApplicationMutation({
          application: JSON.stringify(appData ?? {}),
          applicationId: appData?.applicationId ?? appData?.id,
          applicationTypeId: appData?.applicationTypeId,
        });
      } catch (error) {
        console.error("submitApplication mutation error", error);
        setIsBusy(false);
        setErrorModalState({ message: ErrorMessages.Submit });
        return false;
      }
  
      if (result?.error) {
        setIsBusy(false);
        setErrorModalState({
          errorCode: result.error?.data?.errorCode,
          status: result.error?.status,
          message: result.error?.data?.message || ErrorMessages.Submit,
        });

        return false;
      }

      const data = result.data;

      const savedAppData = data.applicationDataJson
        ? JSON.parse(data.applicationDataJson).applicationData
        : (data.applicationData ?? {});
      updateSavedSnapshot(savedAppData);

      if (!isUninitialized && refetchApplication && !!appData?.uniqueApplicationCode) {
        refetchApplication();
      }

      const { internalStatus } = data;

      setUserApplicationData(() => ({
        programId: data.programId,
        applicationType: data.applicationType,
        applicationTypeId: data.applicationTypeId,
        applicationData: data.applicationData,
        applicationId: data.applicationId ?? data.id,
        internalStatus: data.internalStatus,
        uniqueApplicationCode: data.uniqueApplicationCode,
        applicationData: savedAppData,
      }));

      const isFirstTimeSubmission =
        !prevAppInternalStatus ||
        prevAppInternalStatus === InternalApplicationStatuses.Draft;

      const isSubmittedStatus =
        internalStatus === InternalApplicationStatuses.Submitted ||
        internalStatus === InternalApplicationStatuses.FollowUp ||
        internalStatus === InternalApplicationStatuses.InProgress;

      if (isSubmittedStatus) {
        localStorage.clear();
        sessionStorage.removeItem(USER_APPLICATION_SESSION_KEY);

        // Immediately trigger application list refetch to update header
        dispatch(
          applicationApi.endpoints.getApplicationList.initiate(undefined, {
            forceRefetch: true,
          })
        );

        if (isFirstTimeSubmission) {
          moveToSpecialStep(specialSteps.ApplicationSubmitted);
        } else {
          // Resubmission: show lightweight "Application updated" confirmation
          moveToSpecialStep(specialSteps.ApplicationUpdated);
        }
      } else {
        setIsBusy(false);
      }

      return true;
    },
    [
      userApplicationData,
      currentStep,
      submitApplicationMutation,
      dispatch,
    ]
  );

  // Possibly expand this to continue to uncouple this logic from submitApplication.
  const saveApplication = useCallback(async (applicationOverride = null) => {
    setIsBusy(true);

    const appData = (applicationOverride ?? userApplicationData);

    const { success, errorCode, status, message } = await submitApplicationApi({
      application: {
        ...appData
      },
      applicationId:
        appData?.applicationId ?? appData?.id,
      applicationTypeId: appData?.applicationTypeId,
      user,
    });

    // This is currently only being used in the proxy edit flow.
    // This could be used in the regular flow with more handling for user types and state updates.
    if (!success) {
      console.error(message ?? ErrorMessages.Submit);
      setIsBusy(false);
      setErrorModalState({ errorCode, status, message: message ?? ErrorMessages.Submit });
    }

    return success;
  }, [userApplicationData, currentStep]);

  const withdrawApplication = useCallback(async (applicationId) => {
    setIsBusy(true);

    const { success, errorCode, status, message } = await withdrawApplicationApi({
      applicationId
    });

    setIsBusy(false);

    if (success) {
      updateSavedSnapshot(userApplicationData?.applicationData);
    } else {
      console.error(message ?? ErrorMessages.Withdraw);
      setIsBusy(false);
      setErrorModalState({ errorCode, status, message: message ?? ErrorMessages.Withdraw });
    }

    return success;
  }, [userApplicationData, applicationId]);

  return {
    error,
    version,
    defaults,
    applicationType,
    internalStatus,
    applicationTemplate: template,
    portal,
    currentStep,
    applicationStatus,
    stepData,
    canEdit,
    onSave,
    onNext,
    onPrevious,
    saveStepData,
    isLoggedIn,
    user,
    getStepData,
    numberOfSteps,
    validSteps,
    specialStep,
    finishLater,
    moveToSubmissionRequirement,
    moveBackToApp,
    isBusy,
    setIsBusy,
    errorModalState,
    setErrorModalState,
    uniqueApplicationCode,
    applicationId,
    submitApplication,
    withdrawApplication,
    inReviewMode,
    moveToStep,
    moveToReviewStep,
    userApplicationData,
    copyApplicationData,
    refuseCopyApplicationData,
    replaceUserApplication,
    isEventsEnabled,
    isActivePortal,
    isActiveEvent,
    activeEvents,
    availableEvents,
    selectedEvent,
    programEventMapping,
    isProgramEventMappingLoading,
    programFeatures,
    isProgramFeaturesLoading,
    supportInfo: useProgramSettingsInfo(SettingNames.PublicNotificationsSupportInformation),
    paymentAmountInfo: useProgramSettingsInfo(SettingNames.PaymentAmount),
    statusCallouts: useProgramSettingsInfo(SettingNames.StatusCallouts)?.statusCallouts ?? [],
    voucher,
    benefits,
  };
};